import { BACKEND_URL } from "@/lib/backendUrl";
import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, Download, FileSignature, Gavel, Globe, Loader2, RefreshCw, Sparkles,
  Vote } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle } from "@/components/ui/dialog";
import { Chip, Kpi, Panel } from "@/components/common/Chrome";
import { AnnualFigures } from "@/components/governance/AnnualFigures";
import { api, apiError } from "@/lib/api";

const API_BASE = `${BACKEND_URL}/api`;
const words = (s) => String(s || "").replace(/_/g, " ");
const STATUS = {
  draft: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  issued: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  superseded: "border-border bg-muted text-muted-foreground",
};

const F = ({ label, children, hint }) => (
  <div className="space-y-1.5">
    <Label className="text-[11.5px] text-muted-foreground">{label}</Label>
    {children}
    {hint && <p className="text-[10.5px] leading-relaxed text-muted-foreground">{hint}</p>}
  </div>
);

export const AnnualReport = ({ meetings }) => {
  const [cfg, setCfg] = useState(null);
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(null);
  const [form, setForm] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    const [c, l] = await Promise.all([api.get("/annual-report/config"),
      api.get("/annual-report")]);
    setCfg(c.data);
    setList(l.data);
    setForm((f) => f || { date_from: c.data.last_year.date_from,
      date_to: c.data.last_year.date_to, title: "" });
  }, []);

  useEffect(() => { load().catch((e) => toast.error(apiError(e))); }, [load]);

  const openReport = async (ref) => {
    try {
      const res = await api.get(`/annual-report/${ref}`);
      setOpen(res.data);
    } catch (e) { toast.error(apiError(e)); }
  };

  const run = async (key, fn, message) => {
    setBusy(key);
    try {
      const res = await fn();
      toast.success(message);
      if (res?.data?.report_ref) setOpen(res.data);
      await load();
      setDialog(null);
      return res;
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
    return null;
  };

  if (!cfg || !list) return (
    <Panel title="Annual governance report" testId="panel-annual-loading">
      <p className="text-[12.5px] text-muted-foreground">Opening the reports…</p>
    </Panel>
  );

  const n = open?.narrative || {};
  const openMeetings = (meetings || []).filter((m) => !["minuted", "archived"].includes(m.status));

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Kpi label="Reports issued" value={list.counters.issued} testId="annual-kpi-issued"
             hint={`${list.counters.draft} in draft`} />
        <Kpi label="This year runs" value={cfg.this_year.label} testId="annual-kpi-year"
             hint={`${cfg.this_year.date_from} to ${cfg.this_year.date_to}`} />
        <Kpi label="Signatures required" value={cfg.signatories.length}
             testId="annual-kpi-signatories"
             hint={cfg.signatories.map((s) => s.office).join(", ")} />
        <Kpi label="Issued report is" value={words(cfg.policy.annual_report_publishing)}
             testId="annual-kpi-visibility"
             hint={words(cfg.policy.annual_report_after_issue)} />
      </div>

      {cfg.may_prepare && (
        <Panel title="Roll up a year" testId="panel-annual-prepare"
               kicker="The figures are read off the meetings, resolutions, elections, interests, approvals and the rulebook — nothing is typed twice">
          <div className="grid gap-3 md:grid-cols-4">
            <F label="Year from">
              <Input type="date" data-testid="report-from" value={form?.date_from || ""}
                     onChange={(e) => setForm({ ...form, date_from: e.target.value })} />
            </F>
            <F label="Year to">
              <Input type="date" data-testid="report-to" value={form?.date_to || ""}
                     onChange={(e) => setForm({ ...form, date_to: e.target.value })} />
            </F>
            <div className="md:col-span-2">
              <F label="Title" hint={`Leave it blank and the year label is used. This charity runs on the ${words(cfg.policy.annual_report_year_basis)} year.`}>
                <Input data-testid="report-title" value={form?.title || ""}
                       placeholder={`Annual governance report ${
                         form?.date_from === cfg.this_year.date_from ? cfg.this_year.label
                           : form?.date_from === cfg.last_year.date_from ? cfg.last_year.label
                             : `${form?.date_from || ""} to ${form?.date_to || ""}`}`}
                       onChange={(e) => setForm({ ...form, title: e.target.value })} />
              </F>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" className="h-8 rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                    data-testid="prepare-report" disabled={busy === "prepare"}
                    onClick={() => run("prepare", () => api.post("/annual-report", form),
                      "The year is rolled up — read it before anybody signs")}>
              {busy === "prepare" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                : <Sparkles className="mr-1.5 h-3.5 w-3.5" />} Prepare the report
            </Button>
            <Button size="sm" variant="outline" className="h-8 rounded-full"
                    data-testid="use-this-year"
                    onClick={() => setForm({ ...form, date_from: cfg.this_year.date_from,
                      date_to: cfg.this_year.date_to })}>
              Use {cfg.this_year.label}
            </Button>
          </div>
        </Panel>
      )}

      <Panel title="Annual governance reports" testId="panel-annual-list"
             kicker="One record a year, signed by the office bearers and kept for good">
        {list.items.length === 0 ? (
          <p className="text-[12.5px] text-muted-foreground" data-testid="annual-empty">
            No year has been rolled up yet.
          </p>
        ) : (
          <div className="space-y-2">
            {list.items.map((r) => (
              <button key={r.report_ref} type="button" data-testid={`report-${r.report_ref}`}
                      onClick={() => openReport(r.report_ref)}
                      className={`flex w-full flex-wrap items-center justify-between gap-2 rounded-xl border px-3.5 py-3 text-left transition-colors duration-150 ${
                        open?.report_ref === r.report_ref
                          ? "border-primary/50 bg-primary/5" : "border-border hover:border-primary/30"}`}>
                <span>
                  <span className="block text-[12.5px] font-medium text-foreground">{r.title}</span>
                  <span className="block text-[10.5px] text-muted-foreground">
                    {r.report_ref} · version {r.version} · {r.date_from} to {r.date_to} ·
                    prepared by {r.prepared_by}
                    {r.supersedes ? ` · replaces ${r.supersedes}` : ""}
                  </span>
                </span>
                <span className="flex items-center gap-2">
                  <Chip className="border-border bg-muted text-muted-foreground">
                    {r.signed}/{r.required} signed
                  </Chip>
                  <Chip className={STATUS[r.status]} testId={`report-status-${r.report_ref}`}>
                    {words(r.status)}
                  </Chip>
                </span>
              </button>
            ))}
          </div>
        )}
      </Panel>

      {open && (
        <>
          <Panel title={open.title} testId="panel-annual-open"
                 kicker={`${open.report_ref} · version ${open.version} · ${open.date_from} to ${open.date_to}${open.year_in_progress ? " · the year is still running, so these are the figures to date" : ""}`}
                 actions={(
                   <div className="flex flex-wrap items-center gap-2">
                     <Chip className={STATUS[open.status]} testId="annual-status">
                       {words(open.status)}
                     </Chip>
                     {open.you_may_edit && (
                       <>
                         <Button size="sm" variant="outline" className="h-8 rounded-full"
                                 data-testid="refresh-figures" disabled={busy === "refresh"}
                                 onClick={() => run("refresh",
                                   () => api.post(`/annual-report/${open.report_ref}/refresh`),
                                   "The figures are re-read off the register")}>
                           <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Re-read the year
                         </Button>
                         <Button size="sm" variant="outline" className="h-8 rounded-full"
                                 data-testid="redraft-narrative" disabled={busy === "redraft"}
                                 onClick={() => run("redraft",
                                   () => api.post(`/annual-report/${open.report_ref}/narrative`),
                                   "A fresh draft is ready")}>
                           {busy === "redraft" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                             : <Sparkles className="mr-1.5 h-3.5 w-3.5" />} Draft it again
                         </Button>
                       </>
                     )}
                     {open.you_may_sign && (
                       <Button size="sm" className="h-8 rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                               data-testid="sign-report"
                               onClick={() => setDialog({ kind: "sign", minute: "" })}>
                         <FileSignature className="mr-1.5 h-3.5 w-3.5" /> Sign it
                       </Button>
                     )}
                     {open.may_prepare && open.status === "issued"
                       && open.policy.annual_report_adoption !== "signatures"
                       && !open.adoption?.item_ref && (
                       <Button size="sm" variant="outline" className="h-8 rounded-full"
                               data-testid="adopt-report"
                               onClick={() => setDialog({ kind: "adopt",
                                 meeting_ref: openMeetings[0]?.meeting_ref || "" })}>
                         <Vote className="mr-1.5 h-3.5 w-3.5" /> Put it to the board
                       </Button>
                     )}
                     {open.may_revise && (
                       <Button size="sm" variant="outline" className="h-8 rounded-full"
                               data-testid="revise-report"
                               onClick={() => setDialog({ kind: "revise", reason: "" })}>
                         Issue a correction
                       </Button>
                     )}
                     {open.you_may_propose_publishing && (
                       <Button size="sm" variant="outline" className="h-8 rounded-full"
                               data-testid="propose-publishing"
                               onClick={() => setDialog({ kind: "publish", note: "" })}>
                         <Globe className="mr-1.5 h-3.5 w-3.5" /> Ask to publish it
                       </Button>
                     )}
                     {open.you_may_approve_publishing && (
                       <Button size="sm" className="h-8 rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                               data-testid="approve-publishing"
                               onClick={() => setDialog({ kind: "publish", note: "" })}>
                         <Globe className="mr-1.5 h-3.5 w-3.5" /> Approve publishing
                       </Button>
                     )}
                     {open.you_may_withdraw_publishing && (
                       <Button size="sm" variant="outline" className="h-8 rounded-full"
                               data-testid="withdraw-publishing"
                               onClick={() => setDialog({ kind: "withdraw", note: "" })}>
                         Take it down
                       </Button>
                     )}
                     <a href={`${API_BASE}/annual-report/${open.report_ref}/pdf`} target="_blank"
                        rel="noreferrer" data-testid="annual-pdf">
                       <Button size="sm" variant="outline" className="h-8 rounded-full">
                         <Download className="mr-1.5 h-3.5 w-3.5" /> PDF
                       </Button>
                     </a>
                   </div>
                 )}>
            <div className="grid gap-3 sm:grid-cols-3 xl:grid-cols-6">
              {[["Meetings", `${open.figures.counters.meetings_held}/${open.figures.counters.meetings}`, "meetings"],
                ["Resolutions carried", `${open.figures.counters.resolutions_carried}/${open.figures.counters.resolutions}`, "resolutions"],
                ["Elections", open.figures.counters.elections, "elections"],
                ["Average turnout", `${open.figures.counters.average_turnout}%`, "turnout"],
                ["Interests declared", open.figures.counters.conflicts, "conflicts"],
                ["Above the limit", open.figures.counters.approvals, "approvals"]].map(
                ([label, value, key]) => (
                  <div key={key} className="rounded-xl border border-border bg-card px-3 py-2.5"
                       data-testid={`annual-figure-${key}`}>
                    <p className="kicker">{label}</p>
                    <p className="mt-0.5 font-mono text-[15px] text-foreground">{value}</p>
                  </div>
                ))}
            </div>

            <div className="mt-4 space-y-3">
              <p className="kicker">
                The trustees' narrative · drafted by {n.source || "the board"} · every word can be
                rewritten before it is signed
              </p>
              <Textarea rows={7} data-testid="narrative-summary" value={n.summary || ""}
                        readOnly={!open.you_may_edit}
                        className={!open.you_may_edit ? "bg-muted/50 cursor-default" : ""}
                        onChange={(e) => setOpen({ ...open,
                          narrative: { ...n, summary: e.target.value } })} />
              <Textarea rows={4} data-testid="narrative-achievements" value={n.achievements || ""}
                        readOnly={!open.you_may_edit}
                        className={!open.you_may_edit ? "bg-muted/50 cursor-default" : ""}
                        onChange={(e) => setOpen({ ...open,
                          narrative: { ...n, achievements: e.target.value } })} />
              <Textarea rows={4} data-testid="narrative-attention" value={n.attention || ""}
                        readOnly={!open.you_may_edit}
                        className={!open.you_may_edit ? "bg-muted/50 cursor-default" : ""}
                        onChange={(e) => setOpen({ ...open,
                          narrative: { ...n, attention: e.target.value } })} />
              {open.you_may_edit && (
                <Button size="sm" variant="outline" className="h-8 rounded-full"
                        data-testid="save-narrative" disabled={busy === "save"}
                        onClick={() => run("save", () => api.put(`/annual-report/${open.report_ref}`,
                          { title: open.title, summary: n.summary, achievements: n.achievements,
                            attention: n.attention }), "Your wording is saved")}>
                  Save my wording
                </Button>
              )}
            </div>
          </Panel>

          <Panel title="Signatures" testId="panel-annual-signatures"
                 kicker={`${(open.signatures || []).length} of ${(open.required_signatories || []).length} office bearers have signed`}>
            <div className="space-y-1.5">
              {(open.signatures || []).map((s) => (
                <p key={s.email} className="text-[12px] text-muted-foreground"
                   data-testid={`signature-${s.email}`}>
                  <CheckCircle2 className="mr-1.5 inline h-3.5 w-3.5 text-emerald-500" />
                  <span className="text-foreground">{s.name}</span> — {s.office} · signed{" "}
                  {s.signed_at.slice(0, 10)}{s.minute ? ` — ${s.minute}` : ""}
                </p>
              ))}
              {(open.waiting_on || []).map((s) => (
                <p key={s.email} className="text-[12px] text-muted-foreground"
                   data-testid={`waiting-${s.email}`}>
                  Waiting on <span className="text-foreground">{s.name}</span> — {s.office}
                </p>
              ))}
            </div>
            {open.revision_reason && (
              <p className="mt-3 text-[11.5px] text-muted-foreground" data-testid="revision-reason">
                Issued in place of {open.supersedes}: {open.revision_reason}
              </p>
            )}
            {open.superseded_by && (
              <p className="mt-2 text-[11.5px] text-muted-foreground" data-testid="superseded-by">
                A corrected version has been issued as {open.superseded_by}. This one stays readable
                for the record.
              </p>
            )}
            {open.adoption?.item_ref && (
              <p className="mt-3 text-[12px] text-muted-foreground" data-testid="adoption-status">
                <Gavel className="mr-1.5 inline h-3.5 w-3.5" />
                Put to the board as {open.adoption.item_ref}
                {open.adoption.resolution_ref ? ` · ${open.adoption.resolution_ref}` : ""}
                {open.adoption.status ? ` · ${words(open.adoption.status)}` : ""}
                {open.adoption.outcome?.status
                  ? ` — ${words(open.adoption.outcome.status)} (${open.adoption.outcome.for} for, ${open.adoption.outcome.against} against)`
                  : " — publish the item on the Agenda & resolutions tab and the board can vote on it"}
              </p>
            )}
            {open.status === "issued" && (
              <div className="mt-3 space-y-1" data-testid="publication-status">
                {open.publication?.status === "published" ? (
                  <p className="text-[12px] text-muted-foreground">
                    <Globe className="mr-1.5 inline h-3.5 w-3.5 text-emerald-500" />
                    {"Published by "}{open.publication.proposed_by}{" and approved by "}
                    {open.publication.approved_by}{" on "}
                    {(open.publication.approved_at || "").slice(0, 10)}
                    {" — anyone may read it at "}
                    <a className="text-primary hover:underline" data-testid="annual-public-link"
                       href={`/annual-report/${open.report_ref}`} target="_blank" rel="noreferrer">
                      /annual-report/{open.report_ref}
                    </a>
                  </p>
                ) : open.publication?.status === "proposed" ? (
                  <p className="text-[12px] text-muted-foreground">
                    {open.publication.proposed_by}{" has asked for this report to be published. "}
                    {"Waiting on the President / Chairperson to approve."}
                    {open.publication.proposal_note ? ` — ${open.publication.proposal_note}` : ""}
                  </p>
                ) : open.publication?.status === "withdrawn" ? (
                  <p className="text-[12px] text-muted-foreground">
                    {"Taken down by "}{open.publication.withdrawn_by}{" on "}
                    {(open.publication.withdrawn_at || "").slice(0, 10)}{" — "}
                    {open.publication.withdrawn_reason}
                  </p>
                ) : (
                  <p className="text-[12px] text-muted-foreground">
                    Not published. The Secretary asks and the President / Chairperson approves —
                    two people, never one.
                  </p>
                )}
              </div>
            )}
          </Panel>

          <AnnualFigures figures={open.figures} />
        </>
      )}

      <Dialog open={dialog?.kind === "sign"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-lg" data-testid="sign-dialog">
          <DialogHeader><DialogTitle>Sign the annual governance report</DialogTitle>
            <DialogDescription>
              You are signing in your own name as an office bearer. Once every required signature is
              on it the report is issued and can no longer be edited.
            </DialogDescription></DialogHeader>
          <Textarea rows={3} data-testid="sign-minute" placeholder="Anything for the record"
                    value={dialog?.minute || ""}
                    onChange={(e) => setDialog({ ...dialog, minute: e.target.value })} />
          <DialogFooter>
            <Button data-testid="sign-save" disabled={busy === "sign"}
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                    onClick={() => run("sign",
                      () => api.post(`/annual-report/${open.report_ref}/sign`,
                        { minute: dialog.minute }), "Your signature is on the report")}>
              {busy === "sign" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Sign it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog?.kind === "adopt"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-lg" data-testid="adopt-dialog">
          <DialogHeader><DialogTitle>Ask the board to adopt it</DialogTitle>
            <DialogDescription>
              This puts the report on a meeting's agenda as an ordinary resolution, with the usual
              quorum, majority and voting window.
            </DialogDescription></DialogHeader>
          <F label="Meeting">
            <Select value={dialog?.meeting_ref}
                    onValueChange={(v) => setDialog({ ...dialog, meeting_ref: v })}>
              <SelectTrigger data-testid="adopt-meeting"><SelectValue
                placeholder="Pick a meeting" /></SelectTrigger>
              <SelectContent>
                {openMeetings.map((m) => (
                  <SelectItem key={m.meeting_ref} value={m.meeting_ref}>
                    {m.meeting_ref} · {m.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </F>
          <DialogFooter>
            <Button data-testid="adopt-save" disabled={busy === "adopt" || !dialog?.meeting_ref}
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                    onClick={() => run("adopt",
                      () => api.post(`/annual-report/${open.report_ref}/adopt`,
                        { meeting_ref: dialog.meeting_ref }),
                      "The board has been asked to adopt it")}>
              {busy === "adopt" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Put it on the
              agenda
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog?.kind === "publish"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-lg" data-testid="publish-dialog">
          <DialogHeader><DialogTitle>
            {open?.you_may_approve_publishing ? "Approve publishing this report"
              : "Ask for this report to be published"}
          </DialogTitle>
            <DialogDescription>
              Publishing takes two people: the Secretary asks and the President / Chairperson
              approves. Once both agree, anyone can read the signed report without a sign-in.
            </DialogDescription></DialogHeader>
          <Textarea rows={3} data-testid="publish-note" placeholder="Anything for the record"
                    value={dialog?.note || ""}
                    onChange={(e) => setDialog({ ...dialog, note: e.target.value })} />
          <DialogFooter>
            <Button data-testid="publish-save" disabled={busy === "publish"}
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                    onClick={() => run("publish",
                      () => api.post(`/annual-report/${open.report_ref}/publish`,
                        { note: dialog.note || "" }),
                      open.you_may_approve_publishing ? "The report is public now"
                        : "The President has been asked to approve")}>
              {busy === "publish" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {open?.you_may_approve_publishing ? "Approve it" : "Ask the President"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog?.kind === "withdraw"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-lg" data-testid="withdraw-dialog">
          <DialogHeader><DialogTitle>Take the report down</DialogTitle>
            <DialogDescription>
              Either the Secretary or the President may take it down. Your reason stays on the
              record.
            </DialogDescription></DialogHeader>
          <Textarea rows={3} data-testid="withdraw-note" placeholder="Why is it coming down?"
                    value={dialog?.note || ""}
                    onChange={(e) => setDialog({ ...dialog, note: e.target.value })} />
          <DialogFooter>
            <Button data-testid="withdraw-save"
                    disabled={busy === "withdraw" || (dialog?.note || "").trim().length < 10}
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                    onClick={() => run("withdraw",
                      () => api.post(`/annual-report/${open.report_ref}/unpublish`,
                        { note: dialog.note }), "The report is no longer public")}>
              {busy === "withdraw" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Take it
              down
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog?.kind === "revise"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-lg" data-testid="revise-dialog">
          <DialogHeader><DialogTitle>Issue a corrected version</DialogTitle>
            <DialogDescription>
              The issued report is never altered. A correction is a new version with a written
              reason, and the version it replaces stays readable for good.
            </DialogDescription></DialogHeader>
          <Textarea rows={3} data-testid="revise-reason"
                    placeholder="Why does the report need correcting?"
                    value={dialog?.reason || ""}
                    onChange={(e) => setDialog({ ...dialog, reason: e.target.value })} />
          <DialogFooter>
            <Button data-testid="revise-save"
                    disabled={busy === "revise" || (dialog?.reason || "").trim().length < 10}
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                    onClick={() => run("revise",
                      () => api.post(`/annual-report/${open.report_ref}/revise`,
                        { reason: dialog.reason }),
                      "A new version is open for signature")}>
              {busy === "revise" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Start the
              new version
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
